import { useEffect, useMemo } from 'react';
import { useAccountMargin } from '../../../hooks/useAccountMargin';
import { useDryRun } from '../../../hooks/useDryRunWs';
import { useEnoughLiquidity } from '../../../hooks/useMarketOrderPriceImpact';
import { ProductType } from '../../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import { getAmountError, validateAmount } from '../../../lib/amountError';
import { createOrderDataDto, validateOrderFormData } from '../../../lib/orderHelpers';
import { getNonceInNanoseconds, nowInSeconds } from '../../../lib/time';
import { useTradeFormStore } from '../TradeForm/TradeFormState';
import { useOrderFormData } from './useOrderFormData';

export const useTradeFormAmountError = (product: ProductType) => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const {
    data: { enoughLiquidity },
    isPending: enoughLiquidityPending,
  } = useEnoughLiquidity(product.id);

  const { data: marginData } = useAccountMargin();
  const availableMargin = marginData?.totalAvailable;

  const { dryRun, result: dryRunResult, error: dryRunError, reset } = useDryRun();
  const amountUndefined = useTradeFormStore((s) => s.amount === undefined);
  const orderFormData = useOrderFormData();

  // Note this doesn't mean the order can be placed, it just validates that the order is constructed correctly.
  const validatedOrder = useMemo(() => validateOrderFormData(orderFormData), [orderFormData]);
  const amountSizeErrors = validateAmount(validatedOrder?.amount || 0n, product);

  useEffect(() => {
    if (
      !validatedOrder ||
      !validatedOrder.amount ||
      (validatedOrder.type === 'LIMIT' && !validatedOrder.price)
    ) {
      reset();
      return;
    }
    if (!selectedSubaccount || amountSizeErrors) {
      return;
    }

    const baseData = {
      subaccount: selectedSubaccount.name,
      sender: selectedSubaccount.account,
      onchainId: product.onchainId,
      engineType: product.engineType,
      nonce: getNonceInNanoseconds().toString(),
      signedAt: nowInSeconds(),
    } as const;
    const orderDtoData = createOrderDataDto({
      product,
      subaccountName: selectedSubaccount.name,
      sender: selectedSubaccount.account,
      nonce: baseData.nonce,
      signedAt: baseData.signedAt,
      ...validatedOrder,
    });

    dryRun({ data: orderDtoData }, product);
  }, [
    amountSizeErrors,
    dryRun,
    product,
    selectedSubaccount,
    validatedOrder,
    availableMargin, // Rerun when an available margin changes
    reset,
  ]);

  const getData = () => {
    if (!validatedOrder) {
      return 'Invalid order';
    }
    if (dryRunError) {
      return dryRunError;
    }
    return getAmountError({
      amount: validatedOrder?.amount,
      dryRunResult: dryRunResult,
      product,
      enoughLiquidity,
      orderType: validatedOrder.type,
    });
  };

  const getIsPending = () => {
    if (amountSizeErrors) {
      return false;
    }
    if (enoughLiquidityPending) {
      return true;
    }
    if (!enoughLiquidity && validatedOrder?.type === 'MARKET') {
      return false;
    }

    return !dryRunResult && !dryRunError;
  };
  const error = getData();

  return {
    valid: !getIsPending() && !error,
    errorMessage: amountUndefined ? undefined : error,
  };
};
